import {
  parseQueryAttributeFilters,
  UrlFilter,
} from '@components/modules/ProductGrid/Filter/filtering'
import { refineProductTitle } from '@components/views/Product/helpersv2'
import {
  getProductFeedItems,
  ProductFeedItem,
} from '@features/products/api/get-product-feed-items'
import { getTrackingProductId } from '@features/products/utils'

import { centAmountToAmountV2 } from '@lib/format-price'
import { isAvailableForPurchase } from '@lib/is-available-for-purchase'
import { reqStorage } from '@lib/ssr-req-storage'

import {
  generateMediaUrlV2,
  getCountryConfig,
  getCurrencyFromCountry,
  getRegion,
} from '@lib/utils'
import { formatDateWithOffset } from '@utils/formatDateWithOffset'
import { mapEdgesToItems } from '@utils/map'
import { COUNTRIES, Country } from 'config'
import { GetServerSideProps } from 'next'
import React from 'react'

const createGoogleShoppingFeed = (
  products: ProductFeedItem[],
  bestSellers: BestSellerVariant[],
  country: Country,
  locale: string,
  soldOnline: boolean,
  meta: boolean,
  filter?: UrlFilter[],
  klaviyo?: boolean,
  bestSellingVariantMap?: Set<string>,
) => {
  return `<?xml version="1.0" encoding="UTF-8"?>
  <rss xmlns:g="http://base.google.com/ns/1.0" version="2.0">
  <channel>
      <title>GUBI - Online Store</title>
      <link>https://gubi.com</link>
      <description>GUBI Product feed</description>
      ${products
        .filter((product) => {
          if (!filter) return true

          return filter.every((f) => {
            if (
              f.slug === 'categories' ||
              f.slug === 'designer' ||
              f.slug === 'collection' ||
              f.slug === 'type' ||
              f.slug == 'tags'
            ) {
              return product.tags.find((tag) => f.values.includes(tag))
            }
            return false
          })
        })
        .map((product) =>
          (soldOnline
            ? mapEdgesToItems(product.variants).filter(
                (variant) => variant.availability.availableForPurchase,
              )
            : mapEdgesToItems(product.variants)
          )

            .map((variant) => {
              if (!mapEdgesToItems(variant.media).length) return
              if (!product.description) return
              if (!variant.price) return

              const isCategoryBestseller =
                bestSellingVariantMap?.has(variant.sku) || false

              let name = (
                klaviyo
                  ? product.name
                  : meta
                    ? product.name
                    : refineProductTitle(variant.name).combinedName
              ).replace(/&(?!#?[a-z0-9]+;)/g, ' ')

              if (
                !klaviyo &&
                product.tags.find((tag) => tag === 'col_Bestlite')
              ) {
                name = `GUBI ${
                  name.includes('Bestlite') ? '' : 'Bestlite '
                }${name}`
              } else if (!name.toLowerCase().includes('gubi') && !klaviyo) {
                name = `GUBI ${name}`
              }

              let lifestyleImages = []
              lifestyleImages = mapEdgesToItems(variant.media)
                .filter((image) =>
                  image.fileName?.split('_')[1]?.startsWith('L'),
                )
                .sort((a, b) => {
                  const aNumber = parseInt(a.fileName?.split('_')[1].slice(1))
                  const bNumber = parseInt(b.fileName?.split('_')[1].slice(1))
                  return aNumber - bNumber
                })

              var variantImages = mapEdgesToItems(variant.media).filter(
                (x) => !x.fileName.includes('.mp4'),
              )

              const materialName = variant.name
                .replace(/&(?!#?[a-z0-9]+;)/g, ' ')
                .match(/\((.*)\)/)
                ?.pop()

              const materials = variant.metadata.find(
                (x) => x.key === 'ODOO_SPEC_Materials',
              )?.value

              let colorGroup = null

              const colors = [
                {
                  name: 'Beige',
                  matchers: ['Beige', 'Taupe', 'Camel', 'Sand'],
                },
                { name: 'Black', matchers: ['Black'] },
                { name: 'Blue', matchers: ['Blue'] },
                { name: 'Brown', matchers: ['Brown', 'Cocoa', 'Tan'] },
                {
                  name: 'Green',
                  matchers: ['Green', 'Moss', 'Sage', 'Fennel'],
                },
                { name: 'Gray', matchers: ['Gray', 'Grey'] },
                {
                  name: 'Orange',
                  matchers: ['Orange', 'Pumpkin'],
                },
                { name: 'Pink', matchers: ['Pink', 'Rose', 'Coral'] },
                { name: 'Purple', matchers: ['Purple', 'Aubergine'] },
                { name: 'Red', matchers: ['Red'] },
                {
                  name: 'White',
                  matchers: ['White', 'Bone', 'Cream', 'Sheep'],
                },
                { name: 'Yellow', matchers: ['Yellow'] },
                { name: 'Gold', matchers: ['Gold'] },
                { name: 'Brass', matchers: ['Brass'] },
                { name: 'Copper', matchers: ['Copper'] },
                { name: 'Silver', matchers: ['Silver', 'Chrome'] },
              ]

              if (!colorGroup) {
                colorGroup = colors
                  .filter((color) =>
                    color.matchers.some((matcher) =>
                      variant.name
                        .toLowerCase()
                        .includes(matcher.toLowerCase()),
                    ),
                  )
                  .map((color) => color.name)
                  .slice(0, 3)
                  .join('/')
              }

              let link = `https://gubi.com/${locale}/${country.code.toLowerCase()}/products/${
                product.slug
              }?itemNumber=${variant.sku}`
              const klaviyoTags = []

              if (klaviyo) {
                link = `https://gubi.com/products/${
                  product.slug
                }?itemNumber=${variant.sku}`

                klaviyoTags.push(getRegion(country.code))

                //push the product category
                const tags = product.tags
                  .filter(
                    (x) =>
                      x.startsWith('col_') ||
                      x.startsWith('cat_') ||
                      x.startsWith('designer_') ||
                      x.startsWith('type_'),
                  )
                  ?.map((x) => prettyPrintTag(x))
                klaviyoTags.push(...tags)
              }

              return `
            <item>
              <g:id>${variant.sku}${
                klaviyo ? `_${getRegion(country.code)}` : ''
              }</g:id>
              <g:title>${name}</g:title>
              <g:description>${replaceSpecialChars(
                product.description.substring(0, 5000) ?? '',
              )}</g:description>
              <g:link>${link}</g:link>
              <g:image_link>${replaceSpecialChars(
                klaviyo
                  ? variantImages[0].src
                  : generateMediaUrlV2(variantImages[0].src)?.replace(
                      '{size}',
                      '1920',
                    ),
              )}</g:image_link>
              ${
                lifestyleImages?.length > 0
                  ? `<g:lifestyle_image_link>${replaceSpecialChars(
                      klaviyo
                        ? generateMediaUrlV2(
                            lifestyleImages.find((x) => x).src,
                            true,
                          )
                        : generateMediaUrlV2(
                            lifestyleImages.find((x) => x).src,
                          )?.replace('{size}', '1920'),
                    )}</g:lifestyle_image_link>`
                  : ''
              }
              ${
                variantImages.length > 1
                  ? variantImages
                      .slice(1)
                      .map((image) => {
                        return `<g:additional_image_link>${replaceSpecialChars(
                          klaviyo
                            ? image.src
                            : generateMediaUrlV2(image.src).replace(
                                '{size}',
                                '1920',
                              ),
                        )}</g:additional_image_link>`
                      })
                      .join('')
                  : ''
              }
              ${
                mapEdgesToItems(product.variants).length > 1
                  ? `<g:item_group_id>${getTrackingProductId(product.id, product.metadata)}</g:item_group_id>`
                  : ''
              }
              ${
                //   !klaviyo
                //     ? product.availableShippingMethods
                //         ?.map(
                //           (shippingMethod) => `
                // <g:shipping>
                //   <g:country>${country.alpha2Code}</g:country>
                //   <g:service>${shippingMethod.name.replaceAll(
                //     /&/g,
                //     '&amp;',
                //   )}</g:service>
                //   <g:price>${centAmountToAmount(shippingMethod.price)} ${
                //     shippingMethod.price.currencyCode
                //   }</g:price>
                //   <g:min_handling_time>1</g:min_handling_time>
                //   <g:max_handling_time>5</g:max_handling_time>
                //   <g:min_transit_time>${
                //     shippingMethod.deliveryEstimate.min
                //   }</g:min_transit_time>
                //   <g:max_transit_time>15</g:max_transit_time>
                // </g:shipping>
                // `,
                //         )
                //         .join('')
                //     : ''
                ''
              }
              <g:custom_label_0>${
                isCategoryBestseller
                  ? `bestseller - ${
                      product.tags
                        ?.find((x) => x.startsWith('cat_')) // Find the category tag
                        ?.replace('cat_', '') // Remove the prefix 'cat_'
                        ?.replace(/-/g, ' ') // Replace dashes with spaces
                        ?.trim() ?? '' // Ensure a fallback if undefined
                    }`
                  : ''
              }</g:custom_label_0>
              <g:custom_label_1>${
                isVariantBestSeller(bestSellers ?? [], variant.sku)
                  ? 'bestseller'
                  : ''
              }</g:custom_label_1>
              <g:custom_label_2>${
                product.tags?.find((x) => x.startsWith('col_')) ?? ''
              }</g:custom_label_2>
              <g:custom_label_3>${
                product.tags?.find((x) => x === 'msgtype_New') ? 'New' : ''
              }</g:custom_label_3>
              <g:custom_label_4>${
                variant.price.discountedPrice ? 'on sale' : ''
              }</g:custom_label_4>

              <g:condition>new</g:condition>
              <g:availability>${
                isAvailableForPurchase(variant.availability)
                  ? 'in_stock'
                  : 'out_of_stock'
              }</g:availability>
              <g:price>${centAmountToAmountV2(variant.price.value)} ${
                klaviyo ? '' : variant.price.value.currencyCode
              }</g:price>
              <g:suggested_retail_price>${centAmountToAmountV2(variant.price.value)} ${
                klaviyo ? '' : variant.price.value.currencyCode
              }</g:suggested_retail_price>

              ${
                variant.barcode?.length === 13
                  ? `<g:gtin>${variant.barcode}</g:gtin>`
                  : ''
              }
              ${
                materials
                  ? `<g:material>${materials
                      .split('/')
                      .slice(0, 3)
                      .join('/')
                      .replace(/\&/gm, '&amp;')}</g:material>`
                  : ''
              }
              ${colorGroup ? `<g:color>${colorGroup}</g:color>` : ''}
              ${
                variant.price.discountedPrice
                  ? `<g:sale_price>${centAmountToAmountV2(
                      variant.price.discountedPrice.value,
                    )} ${
                      variant.price.discountedPrice?.value.currencyCode
                    }</g:sale_price>
                    <g:sale_price_effective_date>${formatDateWithOffset(
                      variant.price.discountedPrice.discount.validFrom,
                    )}/${formatDateWithOffset(
                      variant.price.discountedPrice.discount.validUntil,
                    )}</g:sale_price_effective_date>                `
                  : ''
              }
              <g:brand>GUBI</g:brand>
              <g:google_product_category>${
                getTaxonomyId(product.tags.map((tag) => tag)) ?? ''
              }</g:google_product_category>
              ${(() => {
                const categories = product.tags
                  .filter((tag) => tag.startsWith('cat_'))
                  .map((tag) =>
                    tag
                      .replace('cat_', '')
                      .replace(/-/g, ' ')
                      .replace(/&/g, '&amp;'),
                  )

                const types = product.tags
                  .filter((tag) => tag.startsWith('type_'))
                  .map((tag) =>
                    tag
                      .replace('type_', '')
                      .replace(/-/g, ' ')
                      .replace(/&/g, '&amp;'),
                  )

                const outdoorCategory = categories.find((cat) =>
                  cat.toLowerCase().startsWith('outdoor'),
                )
                const otherCategories = categories.filter(
                  (cat) => !cat.toLowerCase().startsWith('outdoor'),
                )

                const portableType = types.find((type) =>
                  type.toLowerCase().includes('portable'),
                )
                const otherTypes = types.filter(
                  (type) => !type.toLowerCase().includes('portable'),
                )

                const finalTypes = portableType
                  ? `Portable ${otherTypes.join(' ')}`
                  : otherTypes.length > 0
                    ? otherTypes[0]
                    : ''

                const finalProductType =
                  `${outdoorCategory ? `Outdoor ` : ''}${otherCategories.join(' ')} &gt;  ${finalTypes}`
                    .replace(/\s+/g, ' ')
                    .trim() + 's'

                return `<g:product_type>${finalProductType}</g:product_type>`
              })()}
              ${
                !klaviyo && product.tags && product.tags.length > 0
                  ? product.tags
                      .filter((tag) => {
                        return (
                          tag.startsWith('cat_') ||
                          tag.startsWith('designer_') ||
                          tag.startsWith('col_') ||
                          tag.startsWith('type_')
                        )
                      })
                      .map((tag) => ({
                        name: tag
                          .replace('cat', 'category')
                          .replace('col', 'collection')
                          .split('_')[0],
                        value:
                          product.name.includes('-') &&
                          product.name
                            .toLowerCase()
                            .includes(tag.split('_')[1]?.toLowerCase())
                            ? tag.split('_')[1]?.replace(/&/gm, 'and')
                            : tag
                                .split('_')[1]
                                .replace(/-/gm, ' ')
                                .replace(/&/gm, '&amp;'),
                      }))
                      .map((tag) => {
                        return `
                    <g:product_detail>
                        <g:section_name>General</g:section_name>
                        <g:attribute_name>${
                          tag.name.charAt(0).toUpperCase() + tag.name.slice(1)
                        }</g:attribute_name>
                      <g:attribute_value>${tag.value}</g:attribute_value>
                    </g:product_detail>`
                      })
                      .join('')
                  : ''
              }
          
              ${
                klaviyo
                  ? `
                  ${materialName ? `<variant>${materialName}</variant>` : ''}
              <tags>${klaviyoTags.join(',')}</tags>
              <inventory_quantity>${
                country.ecommerceEnabled &&
                isAvailableForPurchase(variant.availability)
                  ? variant.availability.availableQuantity
                  : 0
              }</inventory_quantity>
              <inventory_policy>1</inventory_policy>
              `
                  : ''
              }
              ${meta ? `<override>${country.code?.toUpperCase()}</override>` : ''}
            </item>
            `
            })
            .join(''),
        )
        .join('')}
    </channel>
  </rss> 
    `
}

const replaceSpecialChars = (str: string) =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replaceAll(/<\/?[^>]+(>|$)/g, '')

const ShoppingFeed: React.FC = () => null
export const getServerSideProps: GetServerSideProps = async ({
  res,
  req,
  query,
}) =>
  reqStorage.run(req, async () => {
    if (res) {
      const countryParam = query.country as string
      const soldOnline = query.soldOnline === 'true'
      const meta = query.meta === 'true'
      const filters = query.filters as string
      const klaviyo = query.klaviyo === 'true'
      const channel = getCountryConfig(countryParam).channel
      const currency = getCurrencyFromCountry(countryParam)

      const products = await getProductFeedItems({
        channel,
        currency,
      })

      const bestSellers = await getBestSellers()
      const bestSellingCategory = calculateCategoryBestSellers(
        products,
        bestSellers,
      )
      const bestSellingVariantMap = new Set<string>()
      bestSellingCategory.forEach((product) =>
        product.Variants.forEach((variant) => {
          variant.ItemNumbers.forEach((sku) => bestSellingVariantMap.add(sku))
        }),
      )

      const country = COUNTRIES.find(
        (x) => x.code.toLowerCase() === countryParam.toLowerCase(),
      )

      res.setHeader('Content-Type', 'text/xml; charset=utf-8;')
      res.write(
        createGoogleShoppingFeed(
          products,
          bestSellers,
          country,
          'en',
          soldOnline,
          meta,
          filters ? parseQueryAttributeFilters(filters) : undefined,
          klaviyo,
          bestSellingVariantMap,
        ),
      )
      res.end()
    }
    return {
      props: {},
    }
  })

export default ShoppingFeed

export const getTaxonomyId = (tags: string[]) => {
  let max = 0
  let taxonomyId = ''
  taxonomyIds.forEach((tax) => {
    const matches = tax.types.filter((type) => tags.includes(type)).length
    if (matches > max) {
      max = matches
      taxonomyId = tax.id
    }
  })
  return taxonomyId
}

export const taxonomyIds = [
  { types: ['type_Flower-Pot'], id: '721' },
  { types: ['type_Table-Lamp'], id: '4636' },
  { types: ['type_Floor-Lamp'], id: '4636' },
  { types: ['type_Pendant'], id: '4636' },
  { types: ['type_Table-Lamp'], id: '4636' },
  { types: ['type_Wall-Lamp'], id: '4636' },
  { types: ['type_Dining-Chair'], id: '5886' },
  { types: ['type_Lounge-Chair'], id: '443' },
  { types: ['type_Meeting-Chair'], id: '443' },
  { types: ['type_Ottoman'], id: '458' },
  { types: ['type_Pouffe'], id: '443' },
  { types: ['type_Bar-Stool'], id: '1463' },
  { types: ['type_Counter-Stool'], id: '1463' },
  { types: ['type_Sofa'], id: '460' },
  { types: ['type_Coffee-Table'], id: '1395' },
  { types: ['type_Dining-Table'], id: '4355' },
  { types: ['type_Lounge-Table'], id: '6392' },
  { types: ['type_Outdoor'], id: '4299' },
  { types: ['type_Side-Table'], id: '6392' },
  { types: ['type_Dining-Armchair'], id: '5886' },
  { types: ['type_Desk'], id: '4191' },
  { types: ['type_portable-lamp'], id: '4636' },
  { types: ['type_Dresser'], id: '4195' },
  { types: ['type_Sideboard'], id: '447' },
  { types: ['type_Wall-Mirror'], id: '595' },
  { types: ['type_Bar-Chair'], id: '1463' },
  { types: ['type_Bar-Stool'], id: '1463' },
  { types: ['type_Counter-Chair'], id: '1463' },
  { types: ['type_Counter-Stool'], id: '1463' },
  { types: ['type_Stool'], id: '1463' },
  { types: ['type_Candle-holder'], id: '2784' },
  { types: ['type_Shelf'], id: '6372' },
  { types: ['type_Corner-Sofa'], id: '460' },
  { types: ['type_Back-to-back'], id: '460' },
  { types: ['type_Module'], id: '460' },
  { types: ['type_Chaise-Longue'], id: '460' },
  { types: ['type_Bar-Table'], id: '6392' },
  { types: ['type_Console'], id: '6356' },
  { types: ['type_Pouffe'], id: '458' },
  { types: ['type_Bed'], id: '6433' },
  { types: ['type_Rug'], id: '598' },
  { types: ['type_Chandelier'], id: '2249' },
  { types: ['type_Vitrine'], id: '6356' },
  { types: ['type_Bowl'], id: '6457' },
  { types: ['type_Coat-Rack'], id: '2446' },
  { types: ['type_Trolley'], id: '453' },
  { types: ['type_Daybed'], id: '6433' },
  { types: ['type_Dining-Lounge-Chair'], id: '443' },
  { types: ['type_Dining-Sofa'], id: '460' },
  { types: ['type_Lounge-Chair-with-armrest'], id: '443' },
  { types: ['type_Middle-armrest'], id: '443' },
  { types: ['cat_outdoor', 'type_Dining-Chair'], id: '6828' },
  { types: ['cat_outdoor', 'type_Lounge-Chair'], id: '6828' },
  { types: ['cat_outdoor', 'type_Dining-Armchair'], id: '6828' },
  { types: ['cat_outdoor', 'type_Sofa'], id: '4513' },
  { types: ['cat_outdoor', 'type_Ottoman'], id: '6822' },
  { types: ['cat_outdoor', 'type_Coffee-Table'], id: '2684' },
  { types: ['cat_outdoor', 'type_Dining-Table'], id: '2684' },
  { types: ['cat_outdoor', 'type_Side-Table'], id: '2684' },
]

const prettyPrintTag = (tag: string) =>
  tag?.split('_')[1].replace(/-/g, ' ').replace(/&/g, '&amp;')

type BestSellerVariant = {
  sku: string
  soldQuantity: number
}

type Variant = {
  sku: string
  selectedAttributes: string[]
}

type Product = {
  id: string
  name: string
  tags: string[]
  variants: Variant[]
}

type GroupedCategory = {
  [category: string]: {
    productName: string
    productId: string
    category: string
    variants: Variant[]
    quantitySold?: number
  }[]
}
const getBestSellers = async () => {
  const res = await fetch(
    'https://order-integration.gubi.com/commerce/bestsellers',
  )

  const data = await res.json()
  return data?.items as BestSellerVariant[]
}

const isVariantBestSeller = (bestSellers: BestSellerVariant[], sku: string) => {
  const index = bestSellers.findIndex((x) => x.sku === sku)
  return index !== -1 && index < 50
}

const calculateCategoryBestSellers = (
  products: ProductFeedItem[],
  soldVariants: BestSellerVariant[],
) => {
  const groupedCategories = getGroupedCategories(products, soldVariants)

  // Step 4: Process category best-sellers
  const categoryBestSellerProducts = groupedCategories.map((category) => {
    const totalSoldQuantity = category.TotalSumSold

    // Determine the threshold for the bottom 20% of sold units
    const thresholdQuantity = totalSoldQuantity * 0.2

    let cumulativeQuantity = 0

    const sortedProducts = category.Products.sort(
      (a, b) => (b.quantitySold || 0) - (a.quantitySold || 0),
    )

    // Filter products to exclude the bottom 20% based on sold quantities
    const filteredProducts = sortedProducts.filter((product) => {
      cumulativeQuantity += product.quantitySold!
      return cumulativeQuantity <= totalSoldQuantity - thresholdQuantity
    })

    return {
      Category: category.Category,
      Products: filteredProducts,
    }
  })

  // Step 5: Aggregate best-selling products into best-selling variants
  const bestSellingProducts = categoryBestSellerProducts.flatMap((category) =>
    category.Products.map((product) => {
      const groupedVariants = product.variants.reduce(
        (acc, variant) => {
          const key = variant.selectedAttributes.sort().join('')
          if (!acc[key]) acc[key] = []
          acc[key].push(variant.sku)
          return acc
        },
        {} as { [key: string]: string[] },
      )

      const variantsWithQuantities = Object.entries(groupedVariants).map(
        ([_, itemNumbers]) => ({
          ItemNumbers: itemNumbers,
          QuantitySum: itemNumbers.reduce((sum, itemNumber) => {
            const soldVariant = soldVariants.find((sv) => sv.sku === itemNumber)
            return sum + (soldVariant?.soldQuantity ?? 0)
          }, 0),
        }),
      )

      return {
        ProductId: product.productId,
        ProductCategory: product.category,
        Variants: variantsWithQuantities,
        ProductSoldQuantity: variantsWithQuantities.reduce(
          (sum, variant) => sum + variant.QuantitySum,
          0,
        ),
      }
    }),
  )

  // Step 6: Filter best-selling variants
  const bestSellingVariantProducts = bestSellingProducts.map((product) => {
    const totalVariantSoldQuantity = product.Variants.reduce(
      (sum, variant) => sum + variant.QuantitySum,
      0,
    )

    const variantThresholdQuantity = totalVariantSoldQuantity * 0.2

    let cumulativeVariantQuantity = 0

    // Step 3: Sort variants by QuantitySum in descending order
    const sortedVariants = product.Variants.sort(
      (a, b) => b.QuantitySum - a.QuantitySum,
    )

    // Step 4: Filter out the bottom 20% of variants
    const filteredVariants = sortedVariants.filter((variant) => {
      cumulativeVariantQuantity += variant.QuantitySum
      return (
        cumulativeVariantQuantity <=
        totalVariantSoldQuantity - variantThresholdQuantity
      )
    })

    return {
      ProductId: product.ProductId,
      ProductCategory: product.ProductCategory,
      Variants: filteredVariants,
      ProductSoldQuantity: filteredVariants.reduce(
        (sum, variant) => sum + variant.QuantitySum,
        0,
      ),
    }
  })

  return bestSellingVariantProducts
}

const getGroupedCategories = (
  products: ProductFeedItem[],
  soldVariants: BestSellerVariant[],
) => {
  const groupedCategories = products
    .map((product) => {
      const categoryTag = product.tags.find((tag) => tag.includes('cat_'))
      const productCategory = categoryTag

      const variants = mapEdgesToItems(product.variants).map((variant) => ({
        sku: variant.sku,
        selectedAttributes: variant.selectedAttributes.map(
          (attr) => `${attr.name}_${attr.value}`,
        ),
      }))

      return {
        productId: product.id,
        productName: product.name,
        category: productCategory,
        variants: variants,
      }
    })
    // Step 2: Group by product category
    .reduce<GroupedCategory>((acc, product) => {
      if (!acc[product.category]) {
        acc[product.category] = []
      }
      acc[product.category].push(product)
      return acc
    }, {})

  // Step 3: Process each category group
  const result = Object.entries(groupedCategories).map(
    ([categoryKey, products]) => {
      // Step 3.1: Calculate QuantitySold for each product
      const processedProducts = products.map((product) => {
        const quantitySold = product.variants.reduce((sum, variant) => {
          const soldVariant = soldVariants.find((sv) => sv.sku === variant.sku)
          return sum + (soldVariant ? soldVariant.soldQuantity : 0)
        }, 0)

        return {
          ...product,
          quantitySold: quantitySold,
        }
      })

      // Step 3.2: Sort products by QuantitySold in descending order
      const sortedProducts = processedProducts.sort(
        (a, b) => b.quantitySold - a.quantitySold,
      )

      // Step 3.3: Calculate total sum sold for the category
      const totalSumSold = processedProducts.reduce(
        (sum, product) => sum + product.quantitySold,
        0,
      )

      return {
        Category: categoryKey,
        Products: sortedProducts,
        TotalSumSold: totalSumSold,
      }
    },
  )

  return result
}
